'use client';

import { useState, useEffect, useRef, useLayoutEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { X, ChevronLeft } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Z } from '@/lib/z-index';
import { useAnimatedClose } from '@/lib/use-animated-close';
import { SafeClawLogo } from '@/src/components/ui/safeclaw-logo';
import {
	loginWithPassword,
	loginWithPhone,
	register,
	getSlideCaptcha,
	verifySlideCaptcha,
	getOAuthRedirectUrl,
	ApiError,
	LoginNeedCaptchaError,
	RegisterNeedCaptchaError,
} from '@/lib/api';
import type { SlideCaptchaData, OAuthProvider } from '@/lib/api';
import { SlideCaptcha } from '@/src/components/ui/slide-captcha';
import { analytics } from '@/src/utils/analytics';
import { useAuthForm } from '@/lib/hooks/use-auth-form';
import { LoginTabAccount } from './login-tab-account';
import { LoginTabPhone } from './login-tab-phone';
import { RegisterTab } from './register-tab';

type AuthMode = 'login' | 'register';
type LoginTab = 'account' | 'phone';
type RegisterTabType = 'username' | 'phone';

export function LoginModal({ onClose, onLoginSuccess }: { onClose: () => void; onLoginSuccess: () => void }) {
	const [mode, setMode] = useState<AuthMode>('login');
	const [tab, setTab] = useState<LoginTab>('phone');
	const [regTab, setRegTab] = useState<RegisterTabType>('username');

	// Shared fields
	const [phone, setPhone] = useState('');
	const [username, setUsername] = useState('');
	const [password, setPassword] = useState('');
	const [showPassword, setShowPassword] = useState(false);

	// Phone step states
	const [phoneRegStep, setPhoneRegStep] = useState<1 | 2>(1);
	const [phoneLoginStep, setPhoneLoginStep] = useState<1 | 2>(1);

	const { otp, form } = useAuthForm();

	// OAuth
	const [oauthLoading, setOauthLoading] = useState<OAuthProvider | null>(null);

	// 账号密码登录时的滑块验证状态
	const [loginCaptcha, setLoginCaptcha] = useState<SlideCaptchaData | null>(null);
	const [loginCaptchaLoading, setLoginCaptchaLoading] = useState(false);
	const [loginCaptchaStatus, setLoginCaptchaStatus] = useState<'idle' | 'success' | 'error'>('idle');
	/** 已通过滑块验证的参数，用户点击登录时携带提交 */
	const verifiedCaptchaRef = useRef<{ captchaId: string; pointX: number; pointY: number } | null>(null);
	/** 登录接口已返回过 needCaptcha，下次点击登录时应弹出滑块验证 */
	const needLoginCaptchaRef = useRef(false);

	// 账号密码注册时的滑块验证状态
	const [regCaptcha, setRegCaptcha] = useState<SlideCaptchaData | null>(null);
	const [regCaptchaLoading, setRegCaptchaLoading] = useState(false);
	const [regCaptchaStatus, setRegCaptchaStatus] = useState<'idle' | 'success' | 'error'>('idle');
	/** 已通过滑块验证的参数，用户点击注册时携带提交 */
	const verifiedRegCaptchaRef = useRef<{ captchaId: string; pointX: number; pointY: number } | null>(null);

	// Panel height animation
	const panelRef = useRef<HTMLDivElement>(null);
	const [panelHeight, setPanelHeight] = useState<number | undefined>(undefined);

	useLayoutEffect(() => {
		const el = panelRef.current;
		if (!el) return;
		const ro = new ResizeObserver(() => {
			setPanelHeight(el.scrollHeight);
		});
		ro.observe(el);
		return () => ro.disconnect();
	}, []);

	const { closing, requestClose } = useAnimatedClose(onClose, 300);
	const [entered, setEntered] = useState(false);

	useEffect(() => {
		const raf = requestAnimationFrame(() => setEntered(true));
		return () => cancelAnimationFrame(raf);
	}, []);

	const [modeTransitioning, setModeTransitioning] = useState(false);

	const animateTransition = useCallback(
		(callback: () => void) => {
			setModeTransitioning(true);
			form.setError('');
			setTimeout(() => {
				callback();
				setTimeout(() => setModeTransitioning(false), 30);
			}, 200);
		},
		[form],
	);

	const switchMode = useCallback(() => {
		animateTransition(() => {
			setPhoneRegStep(1);
			setPhoneLoginStep(1);
			otp.resetDigits();
			needLoginCaptchaRef.current = false;
			setMode((m) => (m === 'login' ? 'register' : 'login'));
		});
	}, [animateTransition, otp]);

	const switchLoginTab = useCallback(() => {
		animateTransition(() => {
			needLoginCaptchaRef.current = false;
			setTab((t) => (t === 'phone' ? 'account' : 'phone'));
		});
	}, [animateTransition]);

	const switchRegTab = useCallback(() => {
		animateTransition(() => {
			setRegTab((t) => (t === 'username' ? 'phone' : 'username'));
			setPhoneRegStep(1);
			otp.resetDigits();
		});
	}, [animateTransition, otp]);

	/**
	 * 统一加载滑块验证码。
	 * - 首次触发（弹窗未打开）：请求期间不显示弹窗，成功后才打开，避免失败时闪烁。
	 * - 弹窗内刷新（校验失败/手动刷新）：弹窗已在显示，期间用 loading spinner 占位。
	 * 失败时关闭弹窗并把后端 msg 显示在登录表单。
	 * @param keepOverlayWhileLoading 加载期间是否保持弹窗显示（仅在弹窗已打开时为 true）
	 * @returns 是否加载成功
	 */
	const loadLoginCaptcha = useCallback(
		async (keepOverlayWhileLoading = false): Promise<boolean> => {
			if (keepOverlayWhileLoading) {
				setLoginCaptchaLoading(true);
			}
			setLoginCaptchaStatus('idle');
			try {
				const data = await getSlideCaptcha();
				setLoginCaptcha(data);
				return true;
			} catch (err) {
				// 关闭滑块弹窗，把后端 msg（如「验证失败次数过多，请明天再试」）显示在登录表单
				setLoginCaptcha(null);
				const msg = err instanceof ApiError ? err.statusText || '获取验证码失败，请重试' : '获取验证码失败，请重试';
				form.setError(msg);
				form.triggerShake();
				return false;
			} finally {
				setLoginCaptchaLoading(false);
			}
		},
		[form],
	);

	/**
	 * 统一加载注册滑块验证码（逻辑同 loadLoginCaptcha）。
	 * @param keepOverlayWhileLoading 加载期间是否保持弹窗显示（仅在弹窗已打开时为 true）
	 */
	const loadRegisterCaptcha = useCallback(
		async (keepOverlayWhileLoading = false): Promise<boolean> => {
			if (keepOverlayWhileLoading) {
				setRegCaptchaLoading(true);
			}
			setRegCaptchaStatus('idle');
			try {
				const data = await getSlideCaptcha();
				setRegCaptcha(data);
				return true;
			} catch (err) {
				setRegCaptcha(null);
				const msg = err instanceof ApiError ? err.statusText || '获取验证码失败，请重试' : '获取验证码失败，请重试';
				form.setError(msg);
				form.triggerShake();
				return false;
			} finally {
				setRegCaptchaLoading(false);
			}
		},
		[form],
	);

	/**
	 * 注册滑块坐标提交 → 调用后端独立校验
	 * 成功：关闭弹窗并缓存参数，等待用户手动点击注册
	 * 失败：刷新验证码，弹窗保持打开
	 */
	/**
	 * 执行注册 + 自动登录。供 handleAccountSubmit 和 handleRegCaptchaVerify 共用。
	 * 调用前须确保 verifiedRegCaptchaRef 已有值。
	 */
	const doRegister = useCallback(async () => {
		form.setError('');
		form.setLoading(true);
		try {
			const captcha = verifiedRegCaptchaRef.current;
			await register({ username: username.trim(), password }, captcha ?? undefined);
			verifiedRegCaptchaRef.current = null;
			analytics.track('UserRegister', { method: 'username' });
			await loginWithPassword(username.trim(), password);
			onLoginSuccess();
		} catch (err) {
			verifiedRegCaptchaRef.current = null;
			if (err instanceof RegisterNeedCaptchaError) {
				// 兜底：后端仍返回 needCaptcha
				form.setLoading(false);
				await loadRegisterCaptcha();
				return;
			} else if (err instanceof ApiError) {
				form.setError(err.statusText || `操作失败 (${err.status})`);
			} else {
				form.setError('网络异常，请稍后重试');
			}
			form.triggerShake();
		} finally {
			form.setLoading(false);
		}
	}, [username, password, form, onLoginSuccess, loadRegisterCaptcha]);

	const handleRegCaptchaVerify = useCallback(
		async (pointX: number, pointY: number) => {
			if (!regCaptcha) return;
			const captchaId = regCaptcha.captchaId;
			setRegCaptchaStatus('idle');
			try {
				const result = await verifySlideCaptcha({ captchaId, pointX, pointY });
				if (result.success) {
					verifiedRegCaptchaRef.current = { captchaId, pointX, pointY };
					setRegCaptchaStatus('success');
					// 验证通过后延迟一会儿关闭弹窗，随即直接调用注册接口
					setTimeout(() => {
						setRegCaptcha(null);
						setRegCaptchaStatus('idle');
						void doRegister();
					}, 400);
				} else {
					setRegCaptchaStatus('error');
					setTimeout(() => {
						void loadRegisterCaptcha(true);
					}, 800);
				}
			} catch {
				setRegCaptchaStatus('error');
				setTimeout(() => {
					void loadRegisterCaptcha(true);
				}, 800);
			}
		},
		[regCaptcha, loadRegisterCaptcha, doRegister],
	);

	// Submit handler for account-based forms (login account tab, register username tab)
	const handleAccountSubmit = useCallback(
		async (e: React.FormEvent) => {
			e.preventDefault();
			form.setError('');
			form.setLoading(true);

			try {
				if (mode === 'login') {
					if (!username.trim() || !password.trim()) {
						form.setError('请输入用户名和密码');
						form.triggerShake();
						form.setLoading(false);
						return;
					}
					const verified = verifiedCaptchaRef.current;
					await loginWithPassword(username.trim(), password, verified ?? undefined);
					// 登录成功后清除已用的验证码参数
					verifiedCaptchaRef.current = null;
					onLoginSuccess();
				} else {
					if (!username.trim() || !password.trim()) {
						form.setError('请输入用户名和密码');
						form.triggerShake();
						form.setLoading(false);
						return;
					}
					// 注册前必须先完成滑块验证，直接弹出验证弹窗；验证通过后由 handleRegCaptchaVerify 自动调用注册
					form.setLoading(false);
					await loadRegisterCaptcha();
					return;
				}
			} catch (err) {
				if (err instanceof LoginNeedCaptchaError) {
					verifiedCaptchaRef.current = null;
					if (needLoginCaptchaRef.current) {
						// 第二次点击：用户已看到过错误提示，现在弹出滑块验证
						needLoginCaptchaRef.current = false;
						form.setLoading(false);
						await loadLoginCaptcha();
						return;
					}
					// 第一次点击：先显示错误信息，让用户知道密码错误；下次点击再弹滑块
					needLoginCaptchaRef.current = true;
					form.setError(err.msg || '用户名或密码错误');
				} else if (err instanceof ApiError) {
					verifiedCaptchaRef.current = null;
					needLoginCaptchaRef.current = false;
					form.setError(err.statusText || `操作失败 (${err.status})`);
				} else {
					form.setError('网络异常，请稍后重试');
				}
				form.triggerShake();
			} finally {
				form.setLoading(false);
			}
		},
		[mode, username, password, form, onLoginSuccess, loadLoginCaptcha, loadRegisterCaptcha],
	);

	/**
	 * 滑块坐标提交 → 调用后端 verify-slide-captcha 独立校验
	 * 成功：关闭弹窗并缓存参数，等待用户手动点击登录
	 * 失败：刷新验证码，弹窗保持打开（禁止手动关闭）
	 */
	const handleLoginCaptchaVerify = useCallback(
		async (pointX: number, pointY: number) => {
			if (!loginCaptcha) return;
			const captchaId = loginCaptcha.captchaId;
			setLoginCaptchaStatus('idle');
			try {
				const result = await verifySlideCaptcha({ captchaId, pointX, pointY });
				if (result.success) {
					verifiedCaptchaRef.current = { captchaId, pointX, pointY };
					setLoginCaptchaStatus('success');
					setTimeout(() => {
						setLoginCaptcha(null);
						setLoginCaptchaStatus('idle');
					}, 400);
				} else {
					// 后端明确返回 success=false
					setLoginCaptchaStatus('error');
					setTimeout(() => {
						void loadLoginCaptcha(true);
					}, 800);
				}
			} catch {
				// 网络/接口异常同样视为失败，刷新验证码重试
				setLoginCaptchaStatus('error');
				setTimeout(() => {
					void loadLoginCaptcha(true);
				}, 800);
			}
		},
		[loginCaptcha, loadLoginCaptcha],
	);

	// Phone login OTP completion
	const handlePhoneLoginComplete = useCallback(
		async (code: string) => {
			if (form.loading) return;
			form.setLoading(true);
			form.setError('');
			try {
				await loginWithPhone(phone.trim(), code);
				onLoginSuccess();
			} catch (err) {
				form.setError(err instanceof ApiError ? err.statusText || '登录失败' : '网络异常，请稍后重试');
				otp.resetDigits();
				setTimeout(() => otp.digitRefs.current[0]?.focus(), 50);
			} finally {
				form.setLoading(false);
			}
		},
		[form, phone, onLoginSuccess, otp],
	);

	// Phone register OTP completion
	const handlePhoneRegisterComplete = useCallback(
		async (code: string) => {
			if (form.loading) return;
			form.setLoading(true);
			form.setError('');
			try {
				await register({ username: phone.trim(), phone: phone.trim(), code });
				analytics.track('UserRegister', { method: 'phone' });
				await loginWithPhone(phone.trim(), code);
				onLoginSuccess();
			} catch (err) {
				form.setError(err instanceof ApiError ? err.statusText || '注册失败' : '网络异常，请稍后重试');
				otp.resetDigits();
				setTimeout(() => otp.digitRefs.current[0]?.focus(), 50);
			} finally {
				form.setLoading(false);
			}
		},
		[form, phone, onLoginSuccess, otp],
	);

	// Subtitle text
	const subtitle =
		mode === 'login'
			? tab === 'phone' && phoneLoginStep === 2
				? '输入验证码'
				: '登录账户'
			: regTab === 'phone' && phoneRegStep === 2
				? '输入验证码'
				: '注册新账户';

	// OAuth 第三方登录
	const handleOAuthLogin = useCallback(async (provider: OAuthProvider) => {
		if (oauthLoading) return;
		setOauthLoading(provider);
		form.setError('');
		try {
			const { url } = await getOAuthRedirectUrl(provider, window.location.href);
			window.location.href = url;
		} catch (err) {
			form.setError(err instanceof ApiError ? (err.statusText || '跳转失败') : '网络异常，请稍后重试');
			form.triggerShake();
		} finally {
			setOauthLoading(null);
		}
	}, [oauthLoading, form]);

	return (
		<>
			{/* Backdrop */}
			<div
				aria-hidden
				className={`liquid-glass-overlay fixed inset-0 transition-all duration-300 ease-out ${closing ? 'opacity-0 backdrop-blur-none' : entered ? 'opacity-100 backdrop-blur-xl' : 'opacity-0 backdrop-blur-none'
					}`}
				onClick={requestClose}
				style={{ zIndex: Z.MODAL_OVERLAY }}
			/>

			{/* Modal */}
			<div className="fixed inset-0 flex items-center justify-center p-4" style={{ zIndex: Z.MODAL_CONTENT }}>
				<div
					className={cn('relative w-full max-w-[420px] transition-all duration-300', form.shake && 'animate-shake')}
					style={{
						opacity: closing ? 0 : entered ? 1 : 0,
						transform: closing ? 'scale(0.96) translateY(8px)' : entered ? 'scale(1) translateY(0)' : 'scale(0.92) translateY(12px)',
						filter: closing ? 'blur(4px)' : entered ? 'blur(0)' : 'blur(6px)',
						transitionTimingFunction: closing ? 'cubic-bezier(0.4, 0, 1, 1)' : 'cubic-bezier(0.16, 1, 0.3, 1)',
					}}
				>
					<div
						className="liquid-glass-modal transition-[height] duration-300 ease-out overflow-hidden"
						style={{
							borderRadius: 'var(--radius-lg)',
							height: panelHeight ? `${panelHeight}px` : 'auto',
						}}
					>
						<div className="p-7" ref={panelRef}>
							{/* Back — register mode only */}
							{mode === 'register' && (
								<button
									className="absolute left-4 top-4 flex h-8 items-center gap-0.5 rounded-full px-2 text-xs text-muted-foreground hover:text-foreground hover:bg-secondary/60 transition-colors z-10"
									onClick={switchMode}
									type="button"
								>
									<ChevronLeft className="h-3.5 w-3.5" />
									返回
								</button>
							)}

							{/* Close button */}
							<button
								className="absolute right-4 top-4 flex h-8 w-8 items-center justify-center rounded-full text-muted-foreground hover:text-foreground hover:bg-secondary/60 transition-colors z-10"
								onClick={requestClose}
								type="button"
							>
								<X className="h-4 w-4" />
							</button>

							{/* Logo */}
							<div className="flex flex-col items-center gap-2 mb-4">
								<SafeClawLogo className="max-w-[200px]" height={40} />
								<p className="text-sm text-muted-foreground transition-opacity duration-200" style={{ opacity: modeTransitioning ? 0 : 1 }}>
									{subtitle}
								</p>
							</div>

							{/* Animated form content */}
							<div
								className="transition-all duration-200 ease-out"
								style={{
									opacity: modeTransitioning ? 0 : 1,
									transform: modeTransitioning ? 'translateY(6px)' : 'translateY(0)',
									filter: modeTransitioning ? 'blur(2px)' : 'blur(0)',
								}}
							>
								{/* Login: Account tab */}
								{mode === 'login' && tab === 'account' && (
									<LoginTabAccount
										form={form}
										mode="login"
										onSubmit={handleAccountSubmit}
										password={password}
										setPassword={setPassword}
										setShowPassword={setShowPassword}
										setUsername={setUsername}
										showPassword={showPassword}
										username={username}
									/>
								)}

								{/* Login: Phone tab */}
								{mode === 'login' && tab === 'phone' && (
									<LoginTabPhone
										form={form}
										onCodeComplete={handlePhoneLoginComplete}
										otp={otp}
										phone={phone}
										setPhone={setPhone}
										setStep={setPhoneLoginStep}
										step={phoneLoginStep}
									/>
								)}

								{/* Register */}
								{mode === 'register' && (
									<RegisterTab
										form={form}
										onCodeComplete={handlePhoneRegisterComplete}
										onSubmit={handleAccountSubmit}
										otp={otp}
										password={password}
										phone={phone}
										phoneRegStep={phoneRegStep}
										regTab={regTab}
										setPassword={setPassword}
										setPhone={setPhone}
										setPhoneRegStep={setPhoneRegStep}
										setShowPassword={setShowPassword}
										setUsername={setUsername}
										showPassword={showPassword}
										username={username}
									/>
								)}
							</div>

							{/* Bottom links */}
							{mode === 'login' && !(tab === 'phone' && phoneLoginStep === 2) && (
								<div className="flex items-center justify-center gap-3 mt-6">
									<button className="text-xs text-muted-foreground hover:text-foreground transition-colors" onClick={switchLoginTab}>
										{tab === 'phone' ? '使用账号登录' : '使用手机号登录'}
									</button>
									<span className="text-xs text-muted-foreground/40">|</span>
									<button className="text-xs text-muted-foreground hover:text-foreground transition-colors" onClick={switchMode}>
										注册新账户
									</button>
								</div>
							)}
							{mode === 'register' && !(regTab === 'phone' && phoneRegStep === 2) && (
								<div className="text-center mt-6">
									{/* 暂时屏蔽使用验证码注册入口 */}
								</div>
							)}

							{/* Third-party OAuth — only shown on login mode and not in OTP step */}
							{mode === 'login' && !(tab === 'phone' && phoneLoginStep === 2) && (
								<div className="mt-6">
									<div className="flex items-center gap-3 mb-4">
										<div className="flex-1 h-px bg-border/60" />
										<span className="text-xs text-muted-foreground/60">其他登录方式</span>
										<div className="flex-1 h-px bg-border/60" />
									</div>
									<div className="flex items-center justify-center gap-4">
										{/* GitHub */}
										<button
											type="button"
											disabled={oauthLoading !== null}
											onClick={() => void handleOAuthLogin('github')}
											className="flex items-center justify-center w-10 h-10 rounded-full border border-border/60 bg-background/60 hover:bg-secondary/60 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
											title="GitHub 登录"
										>
											{oauthLoading === 'github' ? (
												<div className="w-4 h-4 border-2 border-muted-foreground/30 border-t-foreground rounded-full animate-spin" />
											) : (
												<svg viewBox="0 0 24 24" className="w-5 h-5 fill-current" aria-hidden>
													<path d="M12 2C6.477 2 2 6.484 2 12.017c0 4.425 2.865 8.18 6.839 9.504.5.092.682-.217.682-.483 0-.237-.008-.868-.013-1.703-2.782.605-3.369-1.343-3.369-1.343-.454-1.158-1.11-1.466-1.11-1.466-.908-.62.069-.608.069-.608 1.003.07 1.531 1.032 1.531 1.032.892 1.53 2.341 1.088 2.91.832.092-.647.35-1.088.636-1.338-2.22-.253-4.555-1.113-4.555-4.951 0-1.093.39-1.988 1.029-2.688-.103-.253-.446-1.272.098-2.65 0 0 .84-.27 2.75 1.026A9.564 9.564 0 0 1 12 6.844a9.59 9.59 0 0 1 2.504.337c1.909-1.296 2.747-1.027 2.747-1.027.546 1.379.202 2.398.1 2.651.64.7 1.028 1.595 1.028 2.688 0 3.848-2.339 4.695-4.566 4.943.359.309.678.92.678 1.855 0 1.338-.012 2.419-.012 2.747 0 .268.18.58.688.482A10.02 10.02 0 0 0 22 12.017C22 6.484 17.522 2 12 2Z" />
												</svg>
											)}
										</button>


										{/* WeChat */}
										<button
											type="button"
											disabled={oauthLoading !== null}
											onClick={() => void handleOAuthLogin('wechat')}
											className="flex items-center justify-center w-10 h-10 rounded-full border border-border/60 bg-background/60 hover:bg-secondary/60 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
											title="微信登录"
										>
											{oauthLoading === 'wechat' ? (
												<div className="w-4 h-4 border-2 border-muted-foreground/30 border-t-foreground rounded-full animate-spin" />
											) : (
												<svg viewBox="0 0 24 24" className="w-5 h-5 fill-[#07C160]" aria-hidden>
													<path d="M8.691 2.188C3.891 2.188 0 5.476 0 9.53c0 2.212 1.17 4.203 3.002 5.55a.59.59 0 0 1 .213.665l-.39 1.48c-.019.07-.048.141-.048.213 0 .163.13.295.29.295a.326.326 0 0 0 .167-.054l1.903-1.114a.864.864 0 0 1 .717-.098 10.16 10.16 0 0 0 2.837.403c.276 0 .543-.027.811-.05-.857-2.578.157-4.972 1.932-6.446 1.703-1.415 3.882-1.98 5.853-1.838-.576-3.583-4.196-6.348-8.596-6.348zM5.785 5.991c.642 0 1.162.529 1.162 1.18a1.17 1.17 0 0 1-1.162 1.178A1.17 1.17 0 0 1 4.623 7.17c0-.651.52-1.18 1.162-1.18zm5.813 0c.642 0 1.162.529 1.162 1.18a1.17 1.17 0 0 1-1.162 1.178 1.17 1.17 0 0 1-1.162-1.178c0-.651.52-1.18 1.162-1.18zm5.34 2.867c-1.797-.052-3.746.512-5.28 1.786-1.72 1.428-2.687 3.72-1.78 6.22.942 2.453 3.666 4.229 6.884 4.229.826 0 1.622-.12 2.361-.336a.722.722 0 0 1 .598.082l1.584.926a.272.272 0 0 0 .14.047c.134 0 .24-.111.24-.247 0-.06-.023-.12-.038-.177l-.327-1.233a.582.582 0 0 1-.023-.156.49.49 0 0 1 .201-.398C23.024 18.48 24 17.82 24 16.24c0-2.952-2.931-5.282-7.062-5.382zm-3.007 3.168c.535 0 .969.44.969.982a.976.976 0 0 1-.969.983.976.976 0 0 1-.969-.983c0-.542.434-.982.969-.982zm5.818 0c.535 0 .969.44.969.982a.976.976 0 0 1-.969.983.976.976 0 0 1-.969-.983c0-.542.434-.982.969-.982z" />
												</svg>
											)}
										</button>
									</div>
								</div>
							)}
						</div>
					</div>
				</div>
			</div>

			{/* 账号密码登录滑块验证覆盖层（必须验证通过后自动关闭，不允许手动关闭） */}
			{(loginCaptcha || loginCaptchaLoading) &&
				createPortal(
					<div
						className="fixed inset-0 flex items-center justify-center overflow-y-auto bg-black/40 p-4 animate-in fade-in duration-200"
						style={{ zIndex: Z.MODAL_NESTED }}
					>
						<div className="relative w-full max-w-[460px] bg-background rounded-2xl shadow-xl p-5 animate-in zoom-in-95 duration-200">
							<p className="text-sm font-medium text-foreground mb-4">请完成安全验证</p>
							{loginCaptchaLoading || !loginCaptcha ? (
								<div className="flex items-center justify-center h-[220px]">
									<div className="h-6 w-6 animate-spin rounded-full border-2 border-primary border-t-transparent" />
								</div>
							) : (
								<SlideCaptcha
									captcha={loginCaptcha}
									disabled={form.loading || loginCaptchaStatus === 'success'}
									onRefresh={() => {
										void loadLoginCaptcha(true);
									}}
									onVerify={handleLoginCaptchaVerify}
									status={loginCaptchaStatus}
								/>
							)}
						</div>
					</div>,
					document.body,
				)}

			{/* 账号密码注册滑块验证覆盖层（必须验证通过后自动关闭，不允许手动关闭） */}
			{(regCaptcha || regCaptchaLoading) &&
				createPortal(
					<div
						className="fixed inset-0 flex items-center justify-center overflow-y-auto bg-black/40 p-4 animate-in fade-in duration-200"
						style={{ zIndex: Z.MODAL_NESTED }}
					>
						<div className="relative w-full max-w-[460px] bg-background rounded-2xl shadow-xl p-5 animate-in zoom-in-95 duration-200">
							<p className="text-sm font-medium text-foreground mb-4">请完成安全验证</p>
							{regCaptchaLoading || !regCaptcha ? (
								<div className="flex items-center justify-center h-[220px]">
									<div className="h-6 w-6 animate-spin rounded-full border-2 border-primary border-t-transparent" />
								</div>
							) : (
								<SlideCaptcha
									captcha={regCaptcha}
									disabled={form.loading || regCaptchaStatus === 'success'}
									onRefresh={() => {
										void loadRegisterCaptcha(true);
									}}
									onVerify={handleRegCaptchaVerify}
									status={regCaptchaStatus}
								/>
							)}
						</div>
					</div>,
					document.body,
				)}
		</>
	);
}
